import dictionaryImage from '@/shared/assets/images/feature-paywall/dictionary-visual.png';
import downloadImage from '@/shared/assets/images/feature-paywall/download-visual.png';
import lipSyncImage from '@/shared/assets/images/feature-paywall/lipsync-visual.png';
import promptingImage from '@/shared/assets/images/feature-paywall/prompting-visual.png';
import srtUploadImage from '@/shared/assets/images/feature-paywall/srt-visual.png';
import unlimitedLengthImage from '@/shared/assets/images/feature-paywall/unlimited-visual.png';
import voicePresetsImage from '@/shared/assets/images/feature-paywall/voiceclone-visual.svg';
import { Translate } from '@/shared/i18n/translations';

import { LimitedFeature } from './types';

export const FEATURE_PAYWALL_VARIANTS = {
  download: 'download',
  glossary: 'dictionary',
  lipSync: 'lipSync',
  srtDownload: 'srtDownload',
  srtUpload: 'srtUpload',
  translationDictionary: 'dictionary',
  translationTone: 'prompting',
  unlimitedLength: 'unlimitedLength',
  userPrompting: 'prompting',
  voicePresets: 'voicePresets',
} as const satisfies Partial<Record<LimitedFeature, string>>;

export type FeaturePaywallVariant = (typeof FEATURE_PAYWALL_VARIANTS)[keyof typeof FEATURE_PAYWALL_VARIANTS];

export const getFeaturePaywallVariant = (feature?: LimitedFeature): FeaturePaywallVariant | undefined =>
  feature && feature in FEATURE_PAYWALL_VARIANTS
    ? FEATURE_PAYWALL_VARIANTS[feature as keyof typeof FEATURE_PAYWALL_VARIANTS]
    : undefined;

export const getFeaturePaywallContent = (t: Translate) => ({
  dictionary: {
    title: t('pricing.featurePaywall.dictionary.title'),
    badge: t('pricing.featurePaywall.badge.business'),
    bullets: [
      t('pricing.featurePaywall.dictionary.bullet1'),
      t('pricing.featurePaywall.dictionary.bullet2'),
      t('pricing.featurePaywall.dictionary.bullet3'),
    ],
    image: dictionaryImage,
    note: t('pricing.featurePaywall.dictionary.note'),
    subtitle: t('pricing.featurePaywall.dictionary.subtitle'),
  },
  download: {
    title: t('pricing.featurePaywall.download.title'),
    badge: t('pricing.featurePaywall.badge.all'),
    bullets: [
      t('pricing.featurePaywall.download.bullet1'),
      t('pricing.featurePaywall.download.bullet2'),
      t('pricing.featurePaywall.download.bullet3'),
    ],
    image: downloadImage,
    note: t('pricing.featurePaywall.download.note'),
    subtitle: t('pricing.featurePaywall.download.subtitle'),
  },
  lipSync: {
    title: t('pricing.featurePaywall.lipSync.title'),
    badge: t('pricing.featurePaywall.badge.pro'),
    bullets: [
      t('pricing.featurePaywall.lipSync.bullet1'),
      t('pricing.featurePaywall.lipSync.bullet2'),
      t('pricing.featurePaywall.lipSync.bullet3'),
    ],
    image: lipSyncImage,
    note: t('pricing.featurePaywall.lipSync.note'),
    subtitle: t('pricing.featurePaywall.lipSync.subtitle'),
  },
  prompting: {
    title: t('pricing.featurePaywall.prompting.title'),
    badge: t('pricing.featurePaywall.badge.enterprise'),
    bullets: [
      t('pricing.featurePaywall.prompting.bullet1'),
      t('pricing.featurePaywall.prompting.bullet2'),
      t('pricing.featurePaywall.prompting.bullet3'),
    ],
    image: promptingImage,
    note: t('pricing.featurePaywall.prompting.note'),
    subtitle: t('pricing.featurePaywall.prompting.subtitle'),
  },
  srtDownload: {
    title: t('pricing.upgrade.srtDownload.unlockSrtDownload'),
    badge: t('pricing.featurePaywall.badge.pro'),
    bullets: [
      t('pricing.upgrade.srtDownload.plainTextSubtitleFilesWithTimecodesAndSpoken'),
      t('pricing.upgrade.srtDownload.bothOriginalAndTranslatedSubtitleVersionsWithSpeakers'),
      t('pricing.upgrade.srtDownload.textSegmentsFormattedForVideoPlatformsOrEditing'),
    ],
    image: srtUploadImage,
    note: `${t('pricing.upgrade.glossary.perfectFor')} ${t('pricing.upgrade.srtDownload.creatorsWhoNeedCustomSubtitleVariationsYoutubeUploads')}`,
    subtitle: t('pricing.upgrade.srtDownload.getFlexibleSubtitleFilesYouCanEditCustomize'),
  },
  srtUpload: {
    title: t('pricing.featurePaywall.srtUpload.title'),
    badge: t('pricing.featurePaywall.badge.pro'),
    bullets: [
      t('pricing.featurePaywall.srtUpload.bullet1'),
      t('pricing.featurePaywall.srtUpload.bullet2'),
      t('pricing.featurePaywall.srtUpload.bullet3'),
    ],
    image: srtUploadImage,
    note: t('pricing.featurePaywall.srtUpload.note'),
    subtitle: t('pricing.featurePaywall.srtUpload.subtitle'),
  },
  unlimitedLength: {
    title: t('pricing.featurePaywall.unlimitedLength.title'),
    badge: t('pricing.featurePaywall.badge.all'),
    bullets: [
      t('pricing.featurePaywall.unlimitedLength.bullet1'),
      t('pricing.featurePaywall.unlimitedLength.bullet2'),
      t('pricing.featurePaywall.unlimitedLength.bullet3'),
    ],
    image: unlimitedLengthImage,
    note: t('pricing.featurePaywall.unlimitedLength.note'),
    subtitle: t('pricing.featurePaywall.unlimitedLength.subtitle'),
  },
  voicePresets: {
    title: t('pricing.featurePaywall.voicePresets.title'),
    badge: t('pricing.featurePaywall.badge.pro'),
    bullets: [
      t('pricing.featurePaywall.voicePresets.bullet1'),
      t('pricing.featurePaywall.voicePresets.bullet2'),
      t('pricing.featurePaywall.voicePresets.bullet3'),
    ],
    image: voicePresetsImage,
    note: t('pricing.featurePaywall.voicePresets.note'),
    subtitle: t('pricing.featurePaywall.voicePresets.subtitle'),
  },
});
